import type { VideoRotationDegrees } from './walkthroughVideoRotation';
import { normalizeVideoRotationDegrees } from './walkthroughVideoRotation';

const MAX_THUMB_EDGE = 640;

function loadVideoElement(src: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const cleanup = () => {
      video.onloadeddata = null;
      video.onerror = null;
    };

    video.onloadeddata = () => {
      cleanup();
      resolve(video);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Could not decode video for thumbnail'));
    };
    video.src = src;
  });
}

function seekVideo(video: HTMLVideoElement, timeSeconds: number): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      resolve();
      return;
    }
    const target = Math.min(Math.max(0, timeSeconds), Math.max(0, video.duration - 0.05));
    if (Math.abs(video.currentTime - target) < 0.05) {
      resolve();
      return;
    }
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      reject(new Error('Could not seek video for thumbnail'));
    };
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    video.currentTime = target;
  });
}

function drawRotatedFrame(
  video: HTMLVideoElement,
  rotationDegrees: VideoRotationDegrees,
): HTMLCanvasElement {
  const width = video.videoWidth || 640;
  const height = video.videoHeight || 360;
  const swap = rotationDegrees === 90 || rotationDegrees === 270;
  const sourceW = swap ? height : width;
  const sourceH = swap ? width : height;
  const scale = Math.min(1, MAX_THUMB_EDGE / Math.max(sourceW, sourceH));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sourceW * scale));
  canvas.height = Math.max(1, Math.round(sourceH * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas unavailable');
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotationDegrees * Math.PI) / 180);
  ctx.drawImage(
    video,
    -width * scale / 2,
    -height * scale / 2,
    width * scale,
    height * scale,
  );
  return canvas;
}

async function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to encode thumbnail'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.82,
    );
  });
}

/** Capture a JPEG poster frame from a local video File. */
export async function captureWalkthroughThumbnailFromFile(
  file: File,
  rotationDegrees: number | undefined = 0,
): Promise<Blob | null> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const video = await loadVideoElement(objectUrl);
    const seekTo = Number.isFinite(video.duration) && video.duration > 0
      ? Math.min(1, video.duration * 0.1)
      : 0;
    await seekVideo(video, seekTo);
    const canvas = drawRotatedFrame(video, normalizeVideoRotationDegrees(rotationDegrees));
    return await canvasToJpegBlob(canvas);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Capture a poster from a remote video URL by fetching as a blob first.
 * Avoids tainted-canvas failures when the signed storage URL is cross-origin.
 */
export async function captureWalkthroughThumbnailFromRemoteUrl(
  videoUrl: string,
  rotationDegrees: number | undefined = 0,
  options?: { maxBytes?: number },
): Promise<Blob | null> {
  const maxBytes = options?.maxBytes ?? 80 * 1024 * 1024;
  try {
    const head = await fetch(videoUrl, { method: 'HEAD' }).catch(() => null);
    const contentLength = head?.headers.get('content-length');
    if (contentLength && parseInt(contentLength, 10) > maxBytes) {
      return null;
    }

    const response = await fetch(videoUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (blob.size > maxBytes) return null;
    const type = blob.type || 'video/mp4';
    const file = new File([blob], 'walkthrough-source', { type });
    return captureWalkthroughThumbnailFromFile(file, rotationDegrees);
  } catch {
    return null;
  }
}

/** Capture a JPEG poster frame from an already-loaded video element. */
export async function captureWalkthroughThumbnailFromVideoElement(
  video: HTMLVideoElement,
  rotationDegrees: number | undefined = 0,
): Promise<Blob | null> {
  try {
    if (!video.videoWidth || !video.videoHeight) {
      return null;
    }
    const canvas = drawRotatedFrame(video, normalizeVideoRotationDegrees(rotationDegrees));
    return await canvasToJpegBlob(canvas);
  } catch {
    return null;
  }
}
